import { memo, useEffect, useRef, useState } from "react";
import { PlusOutlined, EnvironmentOutlined, RightOutlined } from "@ant-design/icons";

import T from "@/components/common/T";
import ThemeWalksDrawer from "./ThemeWalksDrawer";
import useLocalStore from "@/hooks/useLocalStore";
import useTranslation from "@/hooks/useTranslation";

// Immersive full-bleed theme tile. Clicking the tile opens the walks drawer;
// the "Create" pill opens the create-walk flow.
const ThemeCard = ({ theme, onCreate }) => {
    const { t } = useTranslation();
    const [walksOpen, setWalksOpen] = useState(false);
    const [errored, setErrored] = useState(false);
    const createdWalk = useLocalStore((s) => s.createdWalk);
    const showImage = Boolean(theme.image) && !errored;
    const count = theme.count;

    const openWalks = () => setWalksOpen(true);

    // A freshly created walk for this theme (via the "Create" pill's
    // create-walk flow) should close this card's walks drawer if it happens
    // to be open. `createdWalk` is persisted in localStorage, so a walk
    // created in a *previous* session is already sitting there on mount —
    // ignore that baseline value entirely, and only react to an id that
    // shows up after this card is on screen (a genuinely fresh creation).
    const initialCreatedWalkId = useRef(createdWalk?.id ?? null);
    const handledCreatedWalkId = useRef(null);
    useEffect(() => {
        if (!walksOpen || !createdWalk?.id) return;
        if (createdWalk.id === initialCreatedWalkId.current) return;
        if (handledCreatedWalkId.current === createdWalk.id) return;
        if (Number(createdWalk?.walk_theme_id) === Number(theme?.id)) {
            handledCreatedWalkId.current = createdWalk.id;
            setWalksOpen(false);
        }
    }, [createdWalk, theme?.id, walksOpen]);

    const handleCreate = (e) => {
        e.stopPropagation();
        // Preselect this theme in the create-walk form (form key: walk_theme_id)
        // and flag that the walk originates from a theme (is_join_walk).
        // walk_theme_name is display-only, shown as the selected theme in the form.
        onCreate?.({ walk_theme_id: theme.id, walk_theme_name: theme.title, is_join_walk: 1 });
    };

    return (
        <>
            <div
                className="jw-theme-tile"
                role="button"
                tabIndex={0}
                onClick={openWalks}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openWalks()}
            >
                {showImage ? (
                    <img className="jw-theme-img" src={theme.image} alt={theme.title} onError={() => setErrored(true)} />
                ) : (
                    <div className="jw-theme-fallback" role="img" aria-label={theme.title}>
                        <EnvironmentOutlined />
                    </div>
                )}
                <div className="jw-theme-grad" />

                <button type="button" className="jw-create-pill" onClick={handleCreate}>
                    <PlusOutlined /> <T k="Join" />
                </button>

                <div className="jw-theme-content">
                    <div className="jw-theme-name">{t(theme.title)}</div>
                    {theme.phrase && <div className="jw-theme-phrase">“{t(theme.phrase)}”</div>}
                    {/* <span className="jw-theme-cta">
                        {count > 0 ? `View ${count} ${count === 1 ? "walk" : "walks"}` : "No walks yet"}
                        <RightOutlined className="jw-arrow" style={{ fontSize: 9 }} />
                    </span> */}
                </div>
            </div>
            <ThemeWalksDrawer theme={theme} open={walksOpen} onClose={() => setWalksOpen(false)} onCreate={onCreate} />
        </>
    );
};

export default memo(ThemeCard);
